<template>
  <div class="v2026-cd" v-if="hasReminder">

    <transition name="v2026-cd-skin">
      <span class="v2026-cd-skin" v-if="mode === 'banner' && (bannerAlwaysOn || isInWindow)" aria-hidden="true">
        <span class="v2026-cd-watermark"></span>
      </span>
    </transition>

    <transition name="v2026-cd-strip">
      <div class="v2026-cd-strip" v-if="mode === 'banner' && (bannerAlwaysOn || isInWindow)" :class="'v2026-cd-strip--' + cdPhase" :title="stripTitle">
        <!-- THREE <i> with STATIC class strings, never one :class binding: js/all.js REPLACES the
             <i> with an <svg>, so Vue's vnode still points at a node that is no longer in the
             document and every later patch aimed at it is silently dropped.
             The keyed WRAPPER SPANS are why the phase swap actually lands. Verified in the browser:
             with the <i> itself carrying the v-if, crossing T+60m left the stopwatch on screen AND
             swallowed the new label, because Vue used the stranded <i> as the insertion reference
             (insert() no-ops when ref.parentNode !== parent). The span is Vue's own element, FA
             never touches it, so remove and insert both resolve against live nodes. -->
        <span class="v2026-cd-glyph" v-if="cdPhase === 'scheduled'" key="cd-g-bell" aria-hidden="true">
          <i class="fas fa-bell"></i>
        </span>
        <span class="v2026-cd-glyph" v-else-if="cdPhase === 'overdue'" key="cd-g-overdue" aria-hidden="true">
          <i class="fas fa-exclamation-circle"></i>
        </span>
        <span class="v2026-cd-glyph" v-else key="cd-g-stopwatch" aria-hidden="true">
          <i class="fas fa-stopwatch"></i>
        </span>
        <span class="v2026-cd-digits" v-if="cdPhase === 'counting'" aria-hidden="true" aria-live="off">{{ digits }}</span>
        <!-- Always rendered so the live region exists BEFORE the text arrives. Its scope stays
             ถึงเวลาแล้ว and NOTHING else — the phase text below is aria-hidden on purpose, or every
             already-overdue card in the list would announce itself on page load. -->
        <span class="v2026-cd-status" role="status" aria-live="polite">{{ cdPhase === 'fired' ? 'ถึงเวลาแล้ว' : '' }}</span>
        <span class="v2026-cd-label" v-if="cdPhase === 'scheduled' || cdPhase === 'overdue'" aria-hidden="true">{{ phaseLabel }}</span>
        <span class="sr-only">{{ srLabel }}</span>
      </div>
    </transition>

    <div class="v2026-card-reminder" v-if="mode === 'bell' && !bannerAlwaysOn && cdState === 'bell'" v-b-tooltip.hover title="บทสนทนานี้มีการแจ้งเตือน">
      <i class="fas fa-bell"></i>
      <span>{{ remindAt | prettyOnlyTime }}</span>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

// ONE window for BOTH sides of the reminder — the hour before and the hour after. Do not inline
// 60 / 3600 / 3600000 anywhere else: not in a template condition, a class binding or a tooltip.
const COUNTDOWN_WINDOW_MS = 60 * 60 * 1000

export default {
  name: 'reminder-countdown-v2026',
  props: {
    // Passed through untouched — no .utc(), no .tz(), no offset maths. moment() already lands in
    // browser-local time, the same parse prettyOnlyTime uses, so the strip and the bell agree.
    remindAt: {
      type: [String, Number, Date],
      default: null
    },
    mode: {
      type: String,
      default: 'banner',
      validator: value => ['banner', 'bell'].indexOf(value) !== -1
    }
  },
  data () {
    return {
      // FALSE restores last round exactly: pink only inside the +/-60m window, grey bell outside it.
      // The banner and the bell markup are both still in the template, gated on this one boolean.
      bannerAlwaysOn: true
    }
  },
  computed: {
    // Shared module-level singleton interval — never a setInterval of its own.
    ...mapState('ticker', ['now']),
    remindMoment () {
      const m = moment(this.remindAt)
      return m.isValid() ? m : null
    },
    // Recomputed from TIMESTAMPS every tick, never a decremented counter: a throttled background
    // tab would make a counter confidently wrong, while a diff self-heals on the next tick.
    diffMs () {
      return this.remindMoment ? this.remindMoment.valueOf() - this.now.getTime() : null
    },
    cdState () {
      const d = this.diffMs
      if (d === null) return 'none'
      if (d > COUNTDOWN_WINDOW_MS) return 'bell' // more than an hour out
      if (d > 0) return 'counting' // [T-60m, T]
      if (d > -COUNTDOWN_WINDOW_MS) return 'fired' // (T, T+60m) — the grace window
      return 'bell' // grace window elapsed -> back to the bell
    },
    isInWindow () {
      return this.cdState === 'counting' || this.cdState === 'fired'
    },
    // Identity is a FACT about the reminder, not about the time. No `now` here on purpose: a
    // far-future reminder must not re-decide whether it is pink 86 400 times a day. Logically
    // identical to the old cdState !== 'none' it replaced on the mount.
    hasReminder () {
      return this.remindMoment !== null
    },
    // A strict REFINEMENT of cdState, not a replacement: cdState === 'bell' <=> cdPhase is
    // 'scheduled' or 'overdue'. Same thresholds, same COUNTDOWN_WINDOW_MS, same semantics — the
    // window still only decides where a COUNTDOWN is meaningful. What is new is that the two
    // halves of 'bell' now have names, because they mean opposite things to an admin: one is
    // "a reminder is coming later", the other is "one came and went and nobody dealt with it".
    cdPhase () {
      const d = this.diffMs
      if (d === null) return 'none'
      if (d > COUNTDOWN_WINDOW_MS) return 'scheduled'
      if (d > 0) return 'counting'
      if (d > -COUNTDOWN_WINDOW_MS) return 'fired'
      return 'overdue'
    },
    // Math.floor, not ceil, so the digits genuinely land on 00:00:00 before flipping to ถึงเวลาแล้ว.
    digits () {
      return this.$options.filters.relativeTimeSpanTicker(Math.max(0, Math.floor(this.diffMs / 1000)))
    },
    clockTime () {
      return this.$options.filters.prettyOnlyTime(this.remindAt)
    },
    // Calendar-day relative word. '' means today, so the common case reads as a bare clock time.
    // DD/MM numeric, never MMM: moment.locale() is never set in this app, so a month name would
    // render English inside a Thai string.
    dayLabel () {
      if (!this.remindMoment) return ''
      const days = this.remindMoment.clone().startOf('day')
        .diff(moment(this.now).startOf('day'), 'days')
      if (days === 0) return ''
      if (days === 1) return 'พรุ่งนี้'
      if (days === -1) return 'เมื่อวาน'
      return this.remindMoment.format('DD/MM')
    },
    // Early-returns for counting/fired BEFORE touching dayLabel, so a counting card pays nothing
    // extra per tick. No trailing น. here — this matches the grey bell it replaces, which showed a
    // bare HH:mm. The น. stays in the tooltip and the screen-reader label.
    phaseLabel () {
      const p = this.cdPhase
      if (p !== 'scheduled' && p !== 'overdue') return ''
      const d = this.dayLabel
      const t = this.clockTime
      if (p === 'scheduled') return d ? `เตือน ${d} ${t}` : `เตือน ${t}`
      return d ? `เลยกำหนด ${d} ${t}` : `เลยกำหนด ${t}`
    },
    // Changes at most 3 times over a card's whole lifetime, never per tick, so a screen reader does
    // not re-announce every second. It carries the meaning that .v2026-cd-label deliberately hides.
    srLabel () {
      const d = this.dayLabel
      if (this.cdPhase === 'overdue') {
        return d
          ? `แจ้งเตือน เลยกำหนดแล้ว ตั้งไว้ ${d} เวลา ${this.clockTime} น.`
          : `แจ้งเตือน เลยกำหนดแล้ว ตั้งไว้เวลา ${this.clockTime} น.`
      }
      if (this.cdPhase === 'scheduled' && d) {
        return `แจ้งเตือน ตั้งไว้ ${d} เวลา ${this.clockTime} น.`
      }
      return `แจ้งเตือน ตั้งไว้เวลา ${this.clockTime} น.`
    },
    stripTitle () {
      const d = this.dayLabel
      if (this.cdPhase === 'fired') {
        return `ถึงเวลาแล้ว — ตั้งไว้เวลา ${this.clockTime} น.`
      }
      if (this.cdPhase === 'overdue') {
        return d
          ? `เลยกำหนดแล้ว — ตั้งไว้ ${d} เวลา ${this.clockTime} น.`
          : `เลยกำหนดแล้ว — ตั้งไว้เวลา ${this.clockTime} น.`
      }
      if (this.cdPhase === 'scheduled' && d) {
        return `แจ้งเตือน ตั้งไว้ ${d} เวลา ${this.clockTime} น.`
      }
      return `แจ้งเตือน ตั้งไว้เวลา ${this.clockTime} น.`
    }
  },
  methods: {
    ...mapActions('ticker', ['startTicker'])
  },
  created () {
    // Idempotent — ticker.js guards its handle, so N callers still share ONE interval app-wide.
    this.startTicker()
  }
}
</script>

<style scoped>
.v2026-cd {
  display: contents;

  --v2026-cd-accent:   var(--v2026-color-count, #FF2D55);
  --v2026-cd-ink:      var(--v2026-color-count-deep, #C81E43);
  --v2026-cd-tint:     var(--v2026-color-count-soft, #FF2D5514);
  --v2026-cd-strip-bg: #FFE8ED;
  --v2026-cd-border:   #FF2D554D;
  --v2026-cd-wm:       #FF2D551A;
}

.v2026-cd-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  height: 30px;
  padding: 0 12px;
  margin: -15px -15px 12px -15px;
  border-radius: 11px 11px 0 0;
  background: var(--v2026-cd-strip-bg);
  overflow: hidden;

  font-size: 14px;
  color: var(--v2026-cd-ink);
}

/* The wrapper is the flex item and the colour owner, so the pigment survives whatever FA does to
   the glyph inside it - the injected <svg> is fill: currentColor and inherits from here. */
.v2026-cd-glyph {
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 1;
  color: var(--v2026-cd-ink);
}

/* Kept class-based and never `> i`: with the CSS-only webfont path (all.css, no js/all.js) the
   element stays an <i> and needs its own size. BOTH names for the third glyph, verified in the
   browser: fa-exclamation-circle is a FA5 ALIAS and js/all.js rewrites it to the FA6 canonical
   fa-circle-exclamation on the <svg> it injects. Drop either name and one path loses the rule. */
.v2026-cd-strip .fa-stopwatch,
.v2026-cd-strip .fa-bell,
.v2026-cd-strip .fa-exclamation-circle,
.v2026-cd-strip .fa-circle-exclamation {
  font-size: 14px;
  line-height: 1;
  color: inherit;
}

.v2026-cd-digits {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  color: var(--v2026-cd-ink);
}

.v2026-cd-status {
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  color: var(--v2026-cd-ink);
}

.v2026-cd-status:empty {
  display: none;
}

.v2026-cd-label {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  color: var(--v2026-cd-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;   /* pathological-value guard only; must never fire on real copy */
}

/* The ONE phase modifier. Identity (skin body + skin border) is constant pink in every phase -
   only the banner escalates, and only for the half of the old `bell` state that means the OPPOSITE
   of the other three. --v2026-color-count-deep is the family's own AA-safe deep, already the ink
   colour of every other phase, so this is a weight change inside the pink, not a new hue.
   White on #C81E43 = 5.64:1 (pass). White on the accent #FF2D55 would be 3.64:1 - do not use it.
   Never `--v2026-cd-strip-bg: var(--v2026-cd-ink)`: a custom property referencing itself on the
   same element is a cycle and resolves to unset. */
.v2026-cd-strip--overdue {
  background: var(--v2026-color-count-deep, #C81E43);
}

.v2026-cd-strip--overdue .v2026-cd-label,
.v2026-cd-strip--overdue .v2026-cd-glyph {
  color: #ffffff;
}

.v2026-cd-skin {
  position: absolute;
  top: -1px;
  right: -1px;
  bottom: -1px;
  left: -1px;
  z-index: -1;
  border: 1px solid var(--v2026-cd-border);
  border-radius: 12px;
  background: var(--v2026-cd-tint);
  overflow: hidden;
  pointer-events: none;
}

.v2026-cd-watermark {
  position: absolute;
  top: 80%;
  right: 5px;
  width: 104px;
  height: 104px;
  margin-top: -52px;
  box-sizing: border-box;
  border: 10px solid var(--v2026-cd-wm);
  border-radius: 50%;
  pointer-events: none;
}

.v2026-cd-watermark::before,
.v2026-cd-watermark::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 50%;
  width: 7px;
  margin-left: -3.5px;
  border-radius: 3.5px;
  background: var(--v2026-cd-wm);
  transform-origin: 50% 100%;
}

.v2026-cd-watermark::before {
  height: 32px;
  transform: rotate(60deg);
}

.v2026-cd-watermark::after {
  height: 22px;
  transform: rotate(-60deg);
}

.v2026-card-reminder {
  margin: 10px 0 0 60px;
  font-size: 11px;
  color: #6c757d;
}

.v2026-card-reminder i {
  margin-right: 4px;
}

.v2026-cd-strip-enter,
.v2026-cd-strip-leave-to {
  height: 0;
  margin-top: 0;
  margin-bottom: 0;
  opacity: 0;
}

.v2026-cd-strip-enter-active,
.v2026-cd-strip-leave-active {
  transition: height 0.15s ease, margin-top 0.15s ease, margin-bottom 0.15s ease, opacity 0.15s ease;
}

.v2026-cd-skin-enter,
.v2026-cd-skin-leave-to {
  opacity: 0;
}

.v2026-cd-skin-enter-active,
.v2026-cd-skin-leave-active {
  transition: opacity 0.15s ease;
}

@media (prefers-reduced-motion: reduce) {
  .v2026-cd-strip-enter-active,
  .v2026-cd-strip-leave-active,
  .v2026-cd-skin-enter-active,
  .v2026-cd-skin-leave-active {
    transition: none;
  }
}
</style>
